import { useState, useEffect } from "react";
import {
  LeftCircleOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  StopOutlined,
  PauseCircleOutlined
} from "@ant-design/icons";
import { Button, Col, Descriptions, Form, Modal, Row, Select, notification } from "antd";
import {
  useCancelInstallationMutation,
  useCreateCustomerConnectLogForInstallationMutation,
  useUpdateCustomerConnectLogForInstallationMutation,
  useGetCustomerConnectLogsForInstallationQuery,
  useLazyGetInstallationByIdQuery,
  useOnHoldInstallationMutation
} from "../../../redux/services/installationAPI";
import { useAppSelector } from "../../../redux/hooks";
import ConnectLogTabel from "../ConnectLogTabel";
import { useNavigate, useParams } from "react-router-dom";
import ErrorDetails from "../../../components/ErrorDetails";
import ConnectLogForm from "../ConnectLogForm";
import {
  localizeDateTime,
  localizeDate,
  customerAddress,
  isUserHasRoleTypes,
  getStatusText
} from "../../../utils";
import { holdOptions, cancelOptions, OldInstallTypeOptions } from "../../../constants/helperConstants";

const PAYMENT_AND_EMAIL_STATUS_AVAILABLE_FROM = new Date("2025-03-01");

const ViewInstallation = (props: any) => {
  const navigate = useNavigate();
  const params = useParams();
  const [form] = Form.useForm();
  const [showFullView, setShowFullView] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCallLogData, setSelectedCallLogData] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);

  const [api, contextHolder] = notification.useNotification();

  const installationId = params?.installationId;

  const [getInstalltionById, {
    data,
    isLoading,
    refetch: refetchInstallationId
  }] = useLazyGetInstallationByIdQuery();

  const installation = data?.installation;

  const { data: customerConnectLogs, refetch: refetchCustomerConnectLogs } =
    useGetCustomerConnectLogsForInstallationQuery(installationId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");

  const [cancel] = useCancelInstallationMutation();

  const [onHold] = useOnHoldInstallationMutation();

  const [onCreateCustomerConnectLogForInstallation] =
    useCreateCustomerConnectLogForInstallationMutation();

  const [onUpdateCustomerConnectLogForInstallation] =
    useUpdateCustomerConnectLogForInstallationMutation();

  const user = useAppSelector<any>((store) => store.auth.user);

  const isClient = user?.user_type === "client";

  const isFinanceController = (user: any) => {
    return isUserHasRoleTypes(user, ["finance_controller"]);
  };

  const isSuperUser = (user: any) => {
    return isUserHasRoleTypes(user, ["super_user"]);
  };

  const handleCancelOrHold = (type: string) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setModalType("");
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (installationId) {
      getInstalltionById(installationId);
    }
  }, [installationId]);

  const onFinish = async (values: any) => {
    if (modalType === "HOLD") {
      try {
        await onHold({ id: installationId, reason: values.reason }).unwrap();
        api.success({
          message: "Installation marked as on hold operation successful",
          placement: "top"
        });
        form.resetFields();
        refetchInstallationId();
        setIsModalOpen(false);
      } catch (error: any) {
        api.info({
          message: "Installation marked as on hold operation failed",
          description: <ErrorDetails errors={error?.data || []} />,
          placement: "top"
        });
      }
    }

    if (modalType === "CANCEL") {
      try {
        await cancel({ id: installationId, reason: values.reason }).unwrap();
        api.success({
          message: "Installation cancelled successfully",
          placement: "top"
        });
        form.resetFields();
        refetchInstallationId();
        setIsModalOpen(false);
      } catch (error: any) {
        api.info({
          message: "Installation cancelled operation failed",
          description: <ErrorDetails errors={error?.data || []} />,
          placement: "top"
        });
      }
    }
  };

  const onAddCallLog = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await onCreateCustomerConnectLogForInstallation(values).unwrap();
      api.success({
        message: "Call log created for installation",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      setLoading(false);
      setShowModal(false);
    } catch (error: any) {
      api.info({
        message: "Create call log for installation operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdateCallLog = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await onUpdateCustomerConnectLogForInstallation(values).unwrap();
      api.success({
        message: "Call log updated for installation",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      setLoading(false);
      setShowModal(false);
    } catch (error: any) {
      api.info({
        message: "Update call log for installation operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdateCallLogModal = (callLog: any) => {
    setSelectedCallLogData(callLog);
    setIsEditMode(true);
    setShowModal(true);
  };

  const onUpdate = (callLog: any) => {
    setSelectedCallLogData(callLog);
    setIsEditMode(true);
    setShowModal(true);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const onGoBack = () => {
    navigate(-1);
  };

  const installType = OldInstallTypeOptions.filter(
    (x) => x.value === data?.installation?.install_type
  )[0]?.label;

  return (
    <>
      {contextHolder}
      {!isLoading && data && (
        <div style={{ paddingBottom: 50 }}>
          <Row style={{ marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Installation Details</h2>
              <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
                Back
              </Button>
            </Col>
          </Row>
          {!isClient && (
            <Row style={{ marginBottom: 20 }}>
              <Col
                sm={24}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <div></div>
                <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                  {data?.installation?.status && data?.installation?.status !== "completed" && (
                    <>
                      <Button icon={<StopOutlined />} onClick={() => handleCancelOrHold("CANCEL")}>
                        Cancel
                      </Button>
                      <Button
                        icon={<PauseCircleOutlined />}
                        onClick={() => handleCancelOrHold("HOLD")}
                      >
                        Hold
                      </Button>
                    </>
                  )}
                  {!isFinanceController(user) && (
                    <Button icon={<PlusCircleOutlined />} onClick={() => setShowModal(true)}>
                      Create Call Connect Log
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          )}

          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Installation Code">
              {data?.installation?.ecb_install_code || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Install Type">{installType || "-"}</Descriptions.Item>
            <Descriptions.Item label="Charger Type">
              {data?.installation?.obc || "-"}
            </Descriptions.Item>
          </Descriptions>
          <br />

          <h3>Customer Details</h3>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Name">
              {data?.installation?.customer_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {data?.installation?.contact_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Booking Address">
              {data?.installation?.booking_address || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Zone">
              {data?.installation?.zone?.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Address">
              {customerAddress(data?.installation)}
            </Descriptions.Item>
            {!isClient && (
              <Descriptions.Item label="EV City">
                {data?.installation?.city?.name || "-"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Customer city of Survey/Installation">
              {data?.installation?.customer_city || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Survey Confirmed">
              {data?.installation?.survey_done_same_day ? "YES" : "NO"}
            </Descriptions.Item>
            {!isClient && (
              <Descriptions.Item label="Customer Rating">
                {data?.installation?.customer_rating
                  ? `${data?.installation?.customer_rating}/5`
                  : "-"}
              </Descriptions.Item>
            )}
          </Descriptions>
          <h3>Vehicle Details</h3>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Dealer">
              {data?.installation?.dealer_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Dealer Loc.">
              {data?.installation?.dealer_location || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Brand Model">
              {data?.installation?.brand_model?.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Model Color">
              {data?.installation?.model_colour || "-"}
            </Descriptions.Item>
          </Descriptions>

          <h3>Timelines</h3>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Status">
              {getStatusText(data?.installation)}
            </Descriptions.Item>
            <br />
            <Descriptions.Item label="Scheduled Date - Time">
              {data?.installation?.scheduled_install_date_time
                ? localizeDateTime(data?.installation?.scheduled_install_date_time)
                : "-"}
            </Descriptions.Item>
            <br />
            <Descriptions.Item label="Brand Booking Date">
              {data?.installation?.brand_booking_date
                ? localizeDate(data?.installation?.brand_booking_date)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="eCB Receive Date">
              {data?.installation?.ecb_receive_date
                ? localizeDate(data?.installation?.ecb_receive_date)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="eCB Booking Date">
              {data?.installation?.ecb_booking_date
                ? localizeDate(data?.installation?.ecb_booking_date)
                : "-"}
            </Descriptions.Item>
            <br />
            <Descriptions.Item label="First Contact">
              {data?.installation?.first_contact_at
                ? localizeDateTime(data?.installation?.first_contact_at)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {data?.installation?.created_at
                ? localizeDateTime(data?.installation?.created_at)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Next Follow Up">
              {data?.installation?.next_follow_up_at
                ? localizeDateTime(data?.installation?.next_follow_up_at)
                : "-"}
            </Descriptions.Item>
            <br />
            <Descriptions.Item label="Start Time">
              {data?.installation?.actual_install_date_time
                ? localizeDateTime(data?.installation?.actual_install_date_time)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="End Time">
              {data?.installation?.install_completed_at
                ? localizeDateTime(data?.installation?.install_completed_at)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="OTP Start Time">
              {data?.installation?.installation_start_otp_at
                ? localizeDateTime(data?.installation?.installation_start_otp_at)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="OTP End Time">
              {data?.installation?.installation_end_otp_at
                ? localizeDateTime(data?.installation?.installation_end_otp_at)
                : "-"}
            </Descriptions.Item>
          </Descriptions>

          {(isFinanceController(user) || isSuperUser(user)) && (
            <>
              <h3>Pricing</h3>
              <Descriptions
                title=""
                bordered
                column={1}
                labelStyle={{ fontWeight: 700, width: 250 }}
                size="middle"
              >
                <Descriptions.Item label="Complementary Cable Length">
                  {data?.installation?.complementary_cable_length || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Pricing Per Meter">
                  {data?.installation?.pricing_per_meter || "-"}
                </Descriptions.Item>
              </Descriptions>

              <br />
              <h3>Amount Details</h3>
              <Descriptions
                title=""
                bordered
                column={1}
                labelStyle={{ fontWeight: 700, width: 250 }}
                size="middle"
              >
                <Descriptions.Item label="Amount To Receive">
                  {data?.installation?.amount_to_receive || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Amount To Refund">
                  {data?.installation?.amount_to_refund || "-"}
                </Descriptions.Item>
              </Descriptions>
            </>
          )}

          {!showFullView && (
            <Button type="link" onClick={() => setShowFullView(true)} style={{ marginTop: "20px" }}>
              Show More
            </Button>
          )}

          {showFullView && (
            <>
              <br />
              <Descriptions
                title=""
                bordered
                column={1}
                labelStyle={{ fontWeight: 700, width: 250 }}
                size="middle"
              >
                {!isClient && (
                  <Descriptions.Item label="Brand Booking Code">
                    {data?.installation?.brand_booking_code || "-"}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Cust. Alt. Contact">
                  {data?.installation?.alt_contact_number || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Cust. Email">
                  {data?.installation?.email || "-"}
                </Descriptions.Item>
                <br />
                <Descriptions.Item label="Record - Cust. Ok?">
                  {data?.installation?.customer_ok_with_recording ? "YES" : "NO"}
                </Descriptions.Item>
                <br />
                <Descriptions.Item label="Model Desc.">
                  {data?.installation?.model_description || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Variant">
                  {data?.installation?.model_variant || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Int Color">
                  {data?.installation?.model_int_color || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Ext Color">
                  {data?.installation?.model_ext_color || "-"}
                </Descriptions.Item>

                <br />

                <Descriptions.Item label="Latitude">
                  {data?.installation?.latitude || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Longitude">
                  {data?.installation?.longitude || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Outstation for installer">
                  {data?.installation?.is_outstation ? "YES" : "NO"}
                </Descriptions.Item>
                <Descriptions.Item label="Outstation for client">
                  {data?.installation?.is_outstation_for_client ? "YES" : "NO"}
                </Descriptions.Item>

                <Descriptions.Item label="Site POC Name">
                  {data?.installation?.site_poc_name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Site POC Contact">
                  {data?.installation?.site_poc_contact || "-"}
                </Descriptions.Item>

                <Descriptions.Item label="Cable Length">
                  {data?.installation?.cable_length_new || data?.installation?.cable_length || "-"}
                </Descriptions.Item>

                <br />

                <Descriptions.Item label="Is this client (OEM) paid - Installation ?">
                  {data?.installation?.customer_billing
                    ? "Client/OEM paid"
                    : "Customer/Dealer paid"}
                </Descriptions.Item>
                <Descriptions.Item label="Any extra material used at installation ?">
                  {data?.installation?.is_extra_material_used ? "Yes" : "No"}
                </Descriptions.Item>
                <Descriptions.Item label="Is there a no show during installation ?">
                  {data?.installation?.is_no_show_during_installation ? "Yes" : "No"}
                </Descriptions.Item>
                <Descriptions.Item label="Commission Number">
                  {data?.installation?.commission_number || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="VIN">{data?.installation?.vin || "-"}</Descriptions.Item>
                {(isFinanceController(user) || isSuperUser(user)) && (
                  <>
                    <Descriptions.Item label="Complementary Cable Length">
                      {data?.installation?.complementary_cable_length || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Pricing Per Meter">
                      {data?.installation?.pricing_per_meter || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Refund status">
                      {new Date(data?.installation?.created_at) >
                        PAYMENT_AND_EMAIL_STATUS_AVAILABLE_FROM &&
                        data?.installation?.status === "completed"
                        ? data?.installation?.payment_received
                          ? "done"
                          : "pending"
                        : "-"}
                    </Descriptions.Item>
                  </>
                )}

                <Descriptions.Item label="Planned Start Date">
                  {data?.installation?.planned_start_date || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Complaint Code">
                  {data?.installation?.complaint_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Complaint Given By">
                  {data?.installation?.complaint_given_by || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Product Type">
                  {data?.installation?.product_type || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="SR Type">
                  {data?.installation?.sr_type || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Email 2">
                  {data?.installation?.email2 || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Source">
                  {data?.installation?.source || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Created By">
                  {data?.installation?.created_by || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Initiated Date">
                  {data?.installation?.brand_initiated_date || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Unique ID">
                  {data?.installation?.brand_unique_id || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Dealer Code">
                  {data?.installation?.dealer_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Dealer Branch Code">
                  {data?.installation?.dealer_branch_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Reshedule ID">
                  {data?.installation?.brand_reschedule_id || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Appointment Taken On">
                  {data?.installation?.appointment_taken_on
                    ? localizeDateTime(data?.installation?.appointment_taken_on)
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Modified By">
                  {data?.installation?.brand_modified_by || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Modified Date">
                  {data?.installation?.brand_modified_date
                    ? localizeDateTime(data?.installation?.brand_modified_date)
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand City Code">
                  {data?.installation?.brand_city_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Booking Code">
                  {data?.installation?.brand_booking_code || "-"}
                </Descriptions.Item>
              </Descriptions>
            </>
          )}

          {showFullView && (
            <Button
              type="link"
              onClick={() => setShowFullView(false)}
              style={{ marginTop: "20px" }}
            >
              Show Less
            </Button>
          )}

          {!isFinanceController(user) && (
            <>
              <ConnectLogTabel
                title="Installation customer connect logs"
                data={customerConnectLogs?.customer_connect_logs || []}
                msil={data?.installation?.brand?.send_reports_to_api}
                onUpdate={onUpdate}
                onUpdateCallLogModal={onUpdateCallLogModal}
              />
            </>
          )}
        </div>
      )}

      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh"
          }}
        >
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </div>
      )}
      <>
        <Modal
          title={`${modalType === "HOLD" ? "Put Installation on Hold" : "Cancel Installation"}`}
          open={isModalOpen}
          onCancel={handleCancel}
          footer={[
            <Button key="cancel" onClick={handleCancel}>
              Cancel
            </Button>,
            <Button form="myForm" key="submit" htmlType="submit" type="primary">
              Submit
            </Button>
          ]}
        >
          <Form
            name="myForm"
            form={form}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            style={{ maxWidth: 600 }}
            initialValues={{ remember: true }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
          >
            <Form.Item
              label={modalType === "HOLD" ? "Reason for puting on hold" : "Reason for cancel"}
              name="reason"
              rules={[{ required: true, message: "Please input reason!" }]}
            >
              <Select
                placeholder="Select status"
                optionFilterProp="children"
                showSearch
                filterOption={(inputValue, option: any) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                }
                options={modalType === "HOLD" ? holdOptions : cancelOptions}
              ></Select>
            </Form.Item>
          </Form>
        </Modal>
        {showModal && (
          <ConnectLogForm
            assignment={installation}
            title={isEditMode ? "Update customer connect logs" : "Create customer connect logs"}
            isOpen={showModal}
            msil={data?.installation?.brand?.send_reports_to_api}
            nextFollowUpRequired={data?.installation?.brand?.next_follow_up_required}
            handleCancel={() => {
              setShowModal(false);
              setIsEditMode(false);
            }}
            isEditMode={isEditMode}
            onAddCallLog={!isEditMode && onAddCallLog}
            onUpdateCallLog={isEditMode && onUpdateCallLog}
            selectedCallLogData={isEditMode && selectedCallLogData}
            booking
          />
        )}
      </>
    </>
  );
};

export default ViewInstallation;
